import 'leaflet/dist/leaflet.css';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo, Fragment } from 'react';
import L from 'leaflet';
import { MapContainer, ImageOverlay, Polygon, Marker, useMap } from 'react-leaflet';
import { api, apiFetch, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/$propertyId/')({
  component: PropertyOverview,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PropertyDetail = {
  id: string;
  name: string;
  role: 'owner' | 'member' | null;
  mapImageUrl: string | null;
  mapImageWidth: number | null;
  mapImageHeight: number | null;
  defaultMowCadenceDays: number;
};

type PropertyDetailResponse = {
  property: PropertyDetail;
  ownerSummary: { id: string; email: string; displayName: string };
  memberCount: number;
};

type ZoneSummary = {
  id: string;
  characterId: string;
  name: string;
  geometry: { type: 'polygon'; points: [number, number][] } | null;
  colorState: 'green' | 'amber' | 'red' | 'neutral';
  lastCompletedAt: string | null;
  daysSinceLastCompletion: number | null;
  effectiveCadenceDays: number;
  deferredUntil: string | null;
  archivedAt: string | null;
};

type DashboardEquipment = {
  id: string;
  name: string;
  archivedAt: string | null;
  isInsideSeason: boolean;
  isCheckInOverdue: boolean;
  nextCheckInDueAt: string | null;
  nextMaintenanceDueAt: string | null;
  notOkFlagCount: number;
  checkInsRequired: boolean;
  outOfCommissionAt: string | null;
  thumbnailUrl: string | null;
};

type ConditionFlag = {
  id: string;
  key: string;
  label: string;
  archivedAt: string | null;
};

type MaintenanceSchedule = {
  id: string;
  name: string;
  triggerKind: string;
  nextDueAt: string | null;
  archivedAt: string | null;
};

type EquipmentListItem = {
  id: string;
  name: string;
  archivedAt: string | null;
  outOfCommissionAt: string | null;
  thumbnailUrl: string | null;
};

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const COLOR_MAP: Record<ZoneSummary['colorState'], string> = {
  green: '#22c55e',
  amber: '#fbbf24',
  red: '#ef4444',
  neutral: '#9ca3af',
};

const STATE_ORDER: Record<ZoneSummary['colorState'], number> = {
  red: 0, amber: 1, green: 2, neutral: 3,
};

// Status pill descriptor: word + color, paired so color is never the only
// signal (requirements 7.3). A future `deferredUntil` overrides colorState.
type ZoneStatus = { word: string; icon: string; pill: string };

function zoneStatus(zone: ZoneSummary): ZoneStatus {
  if (zone.deferredUntil && new Date(zone.deferredUntil) > new Date()) {
    // Gray-green (sage): reads as "handled/fine," softer than the pure-gray
    // "Never" state so it recedes rather than demanding attention.
    return {
      word: 'Deferred',
      icon: '⏸',
      pill: 'text-[#4a6b50] bg-[#e7efe7] dark:text-[#9cbf9c] dark:bg-[#26302a]',
    };
  }
  switch (zone.colorState) {
    case 'red':
      return { word: 'Overdue', icon: '⚠', pill: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950' };
    case 'amber':
      return { word: 'Due soon', icon: '●', pill: 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-950' };
    case 'green':
      return { word: 'Done', icon: '●', pill: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950' };
    default:
      return { word: 'Never', icon: '○', pill: 'text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800' };
  }
}

function daysAgoText(days: number | null): string {
  if (days === null) return 'Never mowed';
  if (days <= 0) return 'Mowed today';
  if (days === 1) return 'Mowed 1 day ago';
  return `Mowed ${days} days ago`;
}

function fullDate(iso: string): string {
  return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function todayStr(): string {
  return new Date().toLocaleDateString('en-CA');
}

// ---------------------------------------------------------------------------
// Polygon label placement
//
// Leaflet's `direction="center"` tooltip anchors at the polygon's bounding-box
// center, which falls OUTSIDE concave / L-shaped / frame zones (e.g. a Trim
// zone wrapping the Back Yard). Instead we compute a "pole of inaccessibility"
// — the interior point farthest from any edge — via a coarse grid search.
// Guaranteed inside the polygon, and visually centered within the widest lobe.
// Coordinates are image-pixel space ([x, y]); a few zones per property makes
// the grid cost negligible.
// ---------------------------------------------------------------------------

type Pt = [number, number];

function pointInPolygon(x: number, y: number, pts: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!;
    const [xj, yj] = pts[j]!;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function minEdgeDist(x: number, y: number, pts: Pt[]): number {
  let m = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[j]!;
    const b = pts[i]!;
    m = Math.min(m, distToSegment(x, y, a[0], a[1], b[0], b[1]));
  }
  return m;
}

function polygonLabelPoint(pts: Pt[]): Pt {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const N = 16;
  let best: Pt | null = null;
  let bestD = -1;
  for (let i = 0; i <= N; i++) {
    for (let j = 0; j <= N; j++) {
      const x = minX + ((maxX - minX) * i) / N;
      const y = minY + ((maxY - minY) * j) / N;
      if (!pointInPolygon(x, y, pts)) continue;
      const d = minEdgeDist(x, y, pts);
      if (d > bestD) { bestD = d; best = [x, y]; }
    }
  }
  if (best) return best;
  // Degenerate (e.g. all collinear) — fall back to vertex average.
  let cx = 0, cy = 0;
  for (const [x, y] of pts) { cx += x; cy += y; }
  return [cx / pts.length, cy / pts.length];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}

function zoneLabelIcon(characterId: string, name: string): L.DivIcon {
  // Styling lives in index.css (.zone-label) so the pill can grow on hover and
  // reveal the full name. iconSize [0,0] keeps Leaflet's wrapper collapsed; the
  // span overflows and centers itself via transform.
  return L.divIcon({
    className: '',
    iconSize: [0, 0],
    html:
      `<span class="zone-label"><span>${escapeHtml(characterId)}</span>` +
      `<span class="zl-name">&nbsp;·&nbsp;${escapeHtml(name)}</span></span>`,
  });
}

function relativeDate(iso: string): string {
  const diffDays = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays}d`;
}

function isUrgent(iso: string): boolean {
  return new Date(iso).getTime() - Date.now() < 7 * 86400000;
}

// ---------------------------------------------------------------------------
// Check-in modal
// ---------------------------------------------------------------------------

function CheckInModal({
  equipmentId,
  propertyId,
  activeFlags,
  hasEngineHoursSchedule,
  onClose,
}: {
  equipmentId: string;
  propertyId: string;
  activeFlags: ConditionFlag[];
  hasEngineHoursSchedule: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [engineHours, setEngineHours] = useState('');
  const [performedOn, setPerformedOn] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [flagValues, setFlagValues] = useState<Record<string, boolean>>({});

  const allFlagsAnswered = activeFlags.every((f) => f.id in flagValues);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(
        `/v1/equipment/${equipmentId}/check-ins`,
        {
          engineHours: hasEngineHoursSchedule ? parseInt(engineHours, 10) : 0,
          performedOn: new Date(performedOn + 'T12:00:00').toISOString(),
          notes: notes.trim() || null,
          flagValues: activeFlags.map((f) => ({ flagId: f.id, value: flagValues[f.id] })),
        },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['check-ins', equipmentId] });
      void qc.invalidateQueries({ queryKey: ['equipment', propertyId, false] });
      onClose();
    },
  });

  const canSubmit =
    (!hasEngineHoursSchedule || engineHours !== '') && allFlagsAnswered && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Log check-in</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Date</label>
          <input
            type="date"
            value={performedOn}
            onChange={(e) => setPerformedOn(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {hasEngineHoursSchedule && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Engine hours</label>
            <input
              type="number"
              min={0}
              value={engineHours}
              onChange={(e) => setEngineHours(e.target.value)}
              placeholder="0"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        {activeFlags.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Condition flags</label>
            <div className="flex flex-col gap-2">
              {activeFlags.map((flag) => {
                const value = flagValues[flag.id];
                return (
                  <div key={flag.id} className="flex items-center gap-3">
                    <span className="text-sm flex-1">{flag.label}</span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setFlagValues((fv) => ({ ...fv, [flag.id]: true }))}
                        className={cn(
                          'px-3 py-1 rounded text-xs border transition-colors',
                          value === true
                            ? 'border-green-400 bg-green-100 text-green-700 dark:bg-green-900 dark:border-green-600 dark:text-green-300'
                            : 'border-border bg-background text-muted-foreground hover:border-green-300',
                        )}
                      >
                        OK
                      </button>
                      <button
                        type="button"
                        onClick={() => setFlagValues((fv) => ({ ...fv, [flag.id]: false }))}
                        className={cn(
                          'px-3 py-1 rounded text-xs border transition-colors',
                          value === false
                            ? 'border-red-400 bg-red-100 text-red-700 dark:bg-red-900 dark:border-red-600 dark:text-red-300'
                            : 'border-border bg-background text-muted-foreground hover:border-red-300',
                        )}
                      >
                        Not OK
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {!allFlagsAnswered && (
              <p className="text-xs text-muted-foreground">Answer all flags to continue.</p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">
            Notes <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations…"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {mutation.isError && (
          <p className="text-sm text-destructive">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Failed to save check-in.'}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Maintenance completion quick row
// ---------------------------------------------------------------------------

function MaintenanceQuickCompletion({
  schedule,
  equipmentId,
  propertyId,
}: {
  schedule: MaintenanceSchedule;
  equipmentId: string;
  propertyId: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [workedOn, setWorkedOn] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const mutation = useMutation({
    mutationFn: () =>
      api.post(
        `/v1/schedules/${schedule.id}/maintenance-completions`,
        {
          workedOn: new Date(workedOn + 'T12:00:00').toISOString(),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules', equipmentId] });
      void qc.invalidateQueries({ queryKey: ['equipment', propertyId, false] });
      setOpen(false);
    },
  });

  const label = schedule.nextDueAt ? relativeDate(schedule.nextDueAt) : null;

  return (
    <div className="pt-2 mt-2 border-t border-border/60">
      <div className="flex items-center gap-2">
        <span className="text-xs flex-1">{schedule.name}</span>
        {label && (
          <span className={cn('text-[10px] font-medium', schedule.nextDueAt && new Date(schedule.nextDueAt) < new Date() ? 'text-red-500' : 'text-amber-500')}>
            {label}
          </span>
        )}
        <button
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Cancel' : 'Complete'}
        </button>
      </div>
      {open && (
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="flex gap-2">
            <div className="flex flex-col gap-0.5 flex-1">
              <label className="text-[10px] text-muted-foreground">Date</label>
              <input type="date" value={workedOn} onChange={(e) => setWorkedOn(e.target.value)}
                className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <label className="text-[10px] text-muted-foreground">Notes (optional)</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="…"
                className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">
              {mutation.error instanceof ApiError ? mutation.error.message : 'Failed.'}
            </p>
          )}
          <Button size="sm" className="h-7 text-xs px-2 self-start" onClick={() => mutation.mutate()}
            disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Mark done'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Equipment quick card
// ---------------------------------------------------------------------------

function EquipmentQuickCard({
  eq,
  propertyId,
}: {
  eq: DashboardEquipment;
  propertyId: string;
}) {
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);

  const { data: flagsData } = useQuery({
    queryKey: ['flags', eq.id],
    queryFn: () =>
      api.get<{ flags: ConditionFlag[] }>(`/v1/equipment/${eq.id}/condition-flags`)
        .then((r) => r.flags),
  });

  const { data: schedulesData } = useQuery({
    queryKey: ['schedules', eq.id],
    queryFn: () =>
      api.get<{ schedules: MaintenanceSchedule[] }>(`/v1/equipment/${eq.id}/schedules`)
        .then((r) => r.schedules),
  });

  const activeFlags = (flagsData ?? []).filter((f) => f.archivedAt === null);
  const activeSchedules = (schedulesData ?? []).filter((s) => s.archivedAt === null);
  const hasEngineHoursSchedule = activeSchedules.some((s) => s.triggerKind === 'engine_hours');
  const hasCheckInItems = activeFlags.length > 0 || activeSchedules.length > 0;

  const urgentSchedules = activeSchedules.filter(
    (s) => s.nextDueAt !== null && isUrgent(s.nextDueAt),
  );

  const checkInOverdueDays =
    eq.isCheckInOverdue && eq.nextCheckInDueAt
      ? Math.max(0, Math.floor((Date.now() - new Date(eq.nextCheckInDueAt).getTime()) / 86400000))
      : 0;
  const nextSchedule = activeSchedules
    .filter((s) => s.nextDueAt)
    .sort((a, b) => new Date(a.nextDueAt!).getTime() - new Date(b.nextDueAt!).getTime())[0];
  const nextDueDate = nextSchedule?.nextDueAt ?? eq.nextMaintenanceDueAt;
  const nextDuePast = nextDueDate !== null && new Date(nextDueDate) < new Date();

  return (
    <>
      {showCheckIn && (
        <CheckInModal
          equipmentId={eq.id}
          propertyId={propertyId}
          activeFlags={activeFlags}
          hasEngineHoursSchedule={hasEngineHoursSchedule}
          onClose={() => setShowCheckIn(false)}
        />
      )}
      <div
        id={`equipment-${eq.id}`}
        className={cn(
          'w-full sm:w-80 rounded-xl border border-border bg-card shadow-sm px-4 py-3 scroll-mt-20',
          eq.outOfCommissionAt && 'opacity-60',
        )}
      >
        <div className="flex items-start gap-2 mb-1">
          {eq.thumbnailUrl && (
            <img
              src={eq.thumbnailUrl}
              alt=""
              className="h-10 w-10 rounded-md object-contain bg-muted shrink-0"
            />
          )}
          <span className="font-medium text-sm flex-1">{eq.name}</span>
          {eq.outOfCommissionAt && (
            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-800">
              ⏸ Out of commission
            </span>
          )}
          {eq.isCheckInOverdue && (
            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950">
              ⚠ Check-in overdue{checkInOverdueDays > 0 ? ` (${checkInOverdueDays}d)` : ''}
            </span>
          )}
          {eq.notOkFlagCount > 0 && (
            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-950">
              ⚠ {eq.notOkFlagCount} flag issue{eq.notOkFlagCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mb-2">
          {nextDueDate ? (
            <span className={cn(nextDuePast && 'text-red-600 dark:text-red-400')}>
              Next: {nextSchedule ? `${nextSchedule.name} · ` : ''}{relativeDate(nextDueDate)}
            </span>
          ) : (
            'No maintenance due'
          )}
          {!eq.isCheckInOverdue && eq.nextCheckInDueAt && (
            <> · Check-in {relativeDate(eq.nextCheckInDueAt)}</>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasCheckInItems && !eq.outOfCommissionAt && (
            <Button
              size="sm"
              variant={eq.isCheckInOverdue ? 'default' : 'outline'}
              onClick={() => setShowCheckIn(true)}
            >
              Check in
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => setShowSchedules((v) => !v)}
          >
            {showSchedules ? '▾' : '▸'} Schedules
          </Button>
        </div>

        {showSchedules && (
          <div className="mt-2">
            {!schedulesData ? (
              <p className="text-xs text-muted-foreground pt-2">Loading…</p>
            ) : urgentSchedules.length === 0 ? (
              <p className="text-xs text-muted-foreground pt-2">No upcoming maintenance in the next 7 days.</p>
            ) : (
              urgentSchedules.map((s) => (
                <MaintenanceQuickCompletion
                  key={s.id}
                  schedule={s}
                  equipmentId={eq.id}
                  propertyId={propertyId}
                />
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Combined Log / Defer modal for zone-specific actions
// ---------------------------------------------------------------------------

// Custom listbox instead of a native <select> so each option can show the
// equipment's thumbnail.
function EquipmentPicker({
  equipment,
  value,
  onChange,
}: {
  equipment: EquipmentListItem[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = equipment.find((e) => e.id === value) ?? null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {selected?.thumbnailUrl && (
          <img src={selected.thumbnailUrl} alt="" className="h-6 w-6 shrink-0 rounded object-contain bg-muted" />
        )}
        <span className={cn('flex-1 truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.name : 'Select equipment…'}
        </span>
        <span aria-hidden className="text-muted-foreground">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-card py-1 shadow-md">
            {equipment.map((eq) => (
              <button
                key={eq.id}
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted',
                  eq.id === value && 'bg-accent',
                )}
                onClick={() => { onChange(eq.id); setOpen(false); }}
              >
                {eq.thumbnailUrl ? (
                  <img src={eq.thumbnailUrl} alt="" className="h-8 w-8 shrink-0 rounded object-contain bg-muted" />
                ) : (
                  <span aria-hidden className="h-8 w-8 shrink-0 rounded bg-muted" />
                )}
                <span className="flex-1 truncate">{eq.name}</span>
                {eq.outOfCommissionAt && (
                  <span className="shrink-0 text-[10px] text-muted-foreground">⏸</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LogOrDeferModal({
  propertyId,
  zones,
  selectedZone,
  initialTab,
  onClose,
}: {
  propertyId: string;
  zones: ZoneSummary[];
  selectedZone: ZoneSummary;
  initialTab: 'log' | 'defer';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'log' | 'defer'>(initialTab);

  const [workedOn, setWorkedOn] = useState(todayStr);
  const [equipmentId, setEquipmentId] = useState('');
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<string>>(() => new Set([selectedZone.id]));
  const [logError, setLogError] = useState<string | null>(null);

  const existingDefer =
    selectedZone.deferredUntil && new Date(selectedZone.deferredUntil) > new Date()
      ? selectedZone.deferredUntil.slice(0, 10)
      : '';
  const [deferDate, setDeferDate] = useState(existingDefer);

  const { data: equipment, isPending: equipPending } = useQuery({
    queryKey: ['equipment', propertyId],
    queryFn: () =>
      api.get<{ equipment: EquipmentListItem[] }>('/v1/properties/' + propertyId + '/equipment')
        .then((r) => r.equipment),
  });
  const activeEquipment = equipment?.filter((e) => !e.archivedAt) ?? [];

  const logMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ completions: unknown[] }>('/v1/properties/' + propertyId + '/zone-completions', {
        method: 'POST',
        body: JSON.stringify({
          zoneIds: [...selectedZoneIds],
          equipmentId,
          workedOn: new Date(workedOn + 'T12:00:00').toISOString(),
        }),
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      onClose();
    },
    onError: (err) => {
      setLogError(err instanceof ApiError ? err.message : 'Something went wrong.');
    },
  });

  const deferMutation = useMutation({
    mutationFn: () =>
      api.post('/v1/zones/' + selectedZone.id + '/defer', {
        deferredUntil: new Date(deferDate + 'T12:00:00').toISOString(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      onClose();
    },
  });

  const tabClass = (t: 'log' | 'defer') =>
    cn(
      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
      tab === t
        ? 'border-foreground text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
              {selectedZone.characterId}
            </span>
            <span className="font-semibold text-sm">{selectedZone.name}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex border-b border-border">
          <button className={tabClass('log')} onClick={() => setTab('log')}>Log</button>
          <button className={tabClass('defer')} onClick={() => setTab('defer')}>Defer</button>
        </div>

        {tab === 'log' && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Date</label>
              <input type="date" value={workedOn} onChange={(e) => setWorkedOn(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Equipment</label>
              {equipPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : (
                <EquipmentPicker
                  equipment={activeEquipment}
                  value={equipmentId}
                  onChange={setEquipmentId}
                />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Zones</label>
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto rounded-md border border-input p-2">
                {zones.map((zone) => (
                  <label key={zone.id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                    <input
                      type="checkbox"
                      checked={selectedZoneIds.has(zone.id)}
                      onChange={(e) => {
                        setSelectedZoneIds((prev) => {
                          const next = new Set(prev);
                          e.target.checked ? next.add(zone.id) : next.delete(zone.id);
                          return next;
                        });
                      }}
                    />
                    <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground">{zone.characterId}</span>
                    <span>{zone.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {logError && <p className="text-sm text-destructive">{logError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => logMutation.mutate()}
                disabled={!equipmentId || selectedZoneIds.size === 0 || logMutation.isPending}
              >
                {logMutation.isPending
                  ? 'Saving…'
                  : `Log ${selectedZoneIds.size} zone${selectedZoneIds.size === 1 ? '' : 's'}`}
              </Button>
            </div>
          </>
        )}

        {tab === 'defer' && (
          <>
            {existingDefer && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Currently deferred until {new Date(selectedZone.deferredUntil!.slice(0, 10) + 'T12:00:00').toLocaleDateString()}. Update the date below to change it.
              </p>
            )}
            {!existingDefer && (
              <p className="text-sm text-muted-foreground">
                Mark this zone as not due until a future date. It will stay green until then.
              </p>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">{existingDefer ? 'Change defer date' : 'Defer until'}</label>
              <input
                type="date"
                value={deferDate}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setDeferDate(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => deferMutation.mutate()}
                disabled={!deferDate || deferMutation.isPending}
              >
                {deferMutation.isPending ? 'Saving…' : 'Set'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-fit map to bounds on load and container resize
// ---------------------------------------------------------------------------

function AutoFit({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    // Fit only when the container has real dimensions. A fitBounds at 0×0 (the
    // map mounted inside a hidden panel, e.g. the mobile List view) corrupts
    // Leaflet's zoom/center and later fits don't fully recover — that was the
    // gray/cropped mobile-map bug. The ResizeObserver fires when the panel
    // first gets a real size (0→N) and drives the first correct fit.
    // zoomSnap=0 on the map lets fitBounds land on an exact fractional zoom
    // instead of rounding up and cropping (the tablet over-zoom bug).
    const fit = () => {
      const c = map.getContainer();
      if (c.clientWidth === 0 || c.clientHeight === 0) return;
      map.invalidateSize(false);
      map.fitBounds(bounds, { padding: [8, 8], animate: false });
    };
    fit();
    const raf = requestAnimationFrame(fit);
    const ro = new ResizeObserver(() => fit());
    ro.observe(map.getContainer());
    map.on('resize', fit);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      map.off('resize', fit);
    };
  }, [map, bounds]);
  return null;
}

// ---------------------------------------------------------------------------
// Zones section: urgency-sorted zone list + optional property map.
// Renders the list whether or not a map image exists (6.7.4 list fallback);
// the map column only appears when one is uploaded.
// ---------------------------------------------------------------------------

function ZonesSection({
  property,
  zones,
}: {
  property: PropertyDetail;
  zones: ZoneSummary[];
}) {
  const qc = useQueryClient();
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  // Desktop-only sidebar; always starts collapsed — expanding lasts only for
  // the current visit (no per-device memory; it kept reading as "open by
  // default" once a device had ever expanded it).
  const [zonesCollapsed, setZonesCollapsed] = useState(true);
  const [modalState, setModalState] = useState<{ zone: ZoneSummary; tab: 'log' | 'defer' } | null>(null);
  // Zones under the last map tap when it lands inside more than one polygon
  // (e.g. a Trim zone wrapping the Back Yard) — prompts the user to disambiguate.
  const [disambig, setDisambig] = useState<ZoneSummary[] | null>(null);
  // Mobile-only view toggle (desktop always shows both). Defaults to Map;
  // a manual switch to List is remembered per device.
  const [mobileView, setMobileView] = useState<'map' | 'list'>(
    () => (localStorage.getItem('mow:zoneView') as 'map' | 'list' | null) ?? 'map',
  );
  useEffect(() => {
    localStorage.setItem('mow:zoneView', mobileView);
  }, [mobileView]);

  const clearDeferMutation = useMutation({
    mutationFn: (id: string) => api.delete('/v1/zones/' + id + '/defer'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zones', property.id] }),
  });

  const imageWidth = property.mapImageWidth ?? 800;
  const imageHeight = property.mapImageHeight ?? 600;
  const mapBounds: L.LatLngBoundsExpression = [[0, 0], [-imageHeight, imageWidth]];

  // Interior label anchor per zone (image-pixel space), recomputed only when
  // geometry changes — not on selection/hover re-renders.
  const labelPoints = useMemo(() => {
    const m = new Map<string, Pt>();
    for (const z of zones) {
      if (z.geometry) m.set(z.id, polygonLabelPoint(z.geometry.points as Pt[]));
    }
    return m;
  }, [zones]);

  const sortedZones = [...zones].sort((a, b) => {
    const so = STATE_ORDER[a.colorState] - STATE_ORDER[b.colorState];
    if (so !== 0) return so;
    const aTime = a.lastCompletedAt ? new Date(a.lastCompletedAt).getTime() : 0;
    const bTime = b.lastCompletedAt ? new Date(b.lastCompletedAt).getTime() : 0;
    return aTime - bTime;
  });

  const zoneListContent = (
    <div className="flex flex-col gap-2">
      {sortedZones.length === 0 && (
        <p className="text-xs text-muted-foreground">No zones yet.</p>
      )}
      {sortedZones.map((zone) => {
        const isActiveDefer = !!zone.deferredUntil && new Date(zone.deferredUntil) > new Date();
        const status = zoneStatus(zone);
        return (
          <div
            key={zone.id}
            className={cn(
              'rounded-lg border px-3 py-2 cursor-pointer transition-colors shadow-sm',
              zone.id === selectedZoneId
                ? 'border-ring ring-1 ring-ring bg-accent'
                : 'border-border bg-card hover:bg-muted',
            )}
            onClick={() => setSelectedZoneId(zone.id)}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground shrink-0">
                {zone.characterId}
              </span>
              <span className="flex-1 truncate font-medium text-xs">{zone.name}</span>
              <span
                className={cn(
                  'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                  status.pill,
                )}
              >
                {status.icon} {status.word}
              </span>
            </div>

            <div
              className="mt-1 text-[10px] text-muted-foreground"
              title={zone.lastCompletedAt ? `Last mowed ${fullDate(zone.lastCompletedAt)}` : undefined}
            >
              {isActiveDefer ? (
                <>Deferred until {fullDate(zone.deferredUntil!)}</>
              ) : (
                <>{daysAgoText(zone.daysSinceLastCompletion)} · every {zone.effectiveCadenceDays}d</>
              )}
            </div>

            <div className="flex items-center gap-1 mt-1.5">
              <Button
                size="xs"
                onClick={(e) => { e.stopPropagation(); setModalState({ zone, tab: 'log' }); }}
              >
                Log
              </Button>
              {isActiveDefer ? (
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={(e) => { e.stopPropagation(); clearDeferMutation.mutate(zone.id); }}
                  disabled={clearDeferMutation.isPending}
                >
                  Clear defer
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={(e) => { e.stopPropagation(); setModalState({ zone, tab: 'defer' }); }}
                >
                  Defer
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const zonesHeading = (
    <h3 className="text-sm font-semibold">
      Zones{zones.length > 0 && ` (${zones.length})`}
    </h3>
  );

  return (
    <div className="flex flex-col gap-3">
      {property.mapImageUrl ? (
        <>
          {/* Map / List toggle — mobile only */}
          <div className="md:hidden px-4">
            <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
              {(['map', 'list'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setMobileView(v)}
                  className={cn(
                    'px-4 py-1.5 rounded capitalize',
                    mobileView === v ? 'bg-muted font-medium' : 'text-muted-foreground',
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div
              className={cn(
                'w-full md:flex-1 rounded-none md:rounded-xl border-y md:border border-border overflow-hidden isolate md:shadow-sm',
                mobileView === 'map' ? '' : 'hidden md:block',
              )}
              style={{ aspectRatio: `${imageWidth} / ${imageHeight}`, maxHeight: '70vh' }}
            >
              <MapContainer
                key={mobileView}
                crs={L.CRS.Simple}
                bounds={mapBounds}
                zoomSnap={0}
                // CRS.Simple defaults minZoom to 0, which clamps fitBounds and
                // prevents zooming OUT to fit an image larger than the
                // container — the over-zoom/crop on tablet & mobile. Allow
                // negative zoom so fitBounds can shrink the image to fit.
                minZoom={-5}
                // Non-interactive: the image is auto-fitted to the container on
                // load/resize (see AutoFit), and any pan/zoom would just mis-center
                // it rather than reveal more detail. Zone clicks still work — layer
                // eventHandlers fire independently of these map-level controls.
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                touchZoom={false}
                boxZoom={false}
                keyboard={false}
                zoomControl={false}
                style={{ width: '100%', height: '100%' }}
              >
                <AutoFit bounds={mapBounds} />
                <ImageOverlay url={property.mapImageUrl} bounds={mapBounds} />
                {zones.filter((z) => z.geometry).map((zone) => {
                  const positions = zone.geometry!.points.map(
                    ([x, y]) => [-y, x] as L.LatLngExpression,
                  );
                  const isSelected = zone.id === selectedZoneId;
                  const onPolygonClick = (e: L.LeafletMouseEvent) => {
                    setSelectedZoneId(zone.id);
                    // Image-space inverse of [x,y] -> [-y, x].
                    const px = e.latlng.lng;
                    const py = -e.latlng.lat;
                    const hits = zones.filter(
                      (z) => z.geometry && pointInPolygon(px, py, z.geometry.points as Pt[]),
                    );
                    if (hits.length > 1) {
                      setDisambig(hits);
                    } else {
                      setModalState({ zone, tab: 'log' });
                    }
                  };
                  const label = labelPoints.get(zone.id);
                  return (
                    <Fragment key={zone.id}>
                      {/* White casing underneath for contrast against the
                          aerial — keeps green-on-grass zones legible. */}
                      <Polygon
                        positions={positions}
                        interactive={false}
                        pathOptions={{
                          color: '#ffffff',
                          weight: isSelected ? 7 : 5,
                          opacity: 0.85,
                          fill: false,
                        }}
                      />
                      <Polygon
                        positions={positions}
                        pathOptions={{
                          color: COLOR_MAP[zone.colorState],
                          fillColor: COLOR_MAP[zone.colorState],
                          // Keep the fill light (25%) so the aerial stays
                          // visible; legibility comes from the white casing +
                          // thicker colored stroke, not from a heavy fill.
                          fillOpacity: isSelected ? 0.45 : 0.25,
                          weight: isSelected ? 4 : 3,
                        }}
                        eventHandlers={{ click: onPolygonClick }}
                      />
                      {label && (
                        <Marker
                          position={[-label[1], label[0]]}
                          icon={zoneLabelIcon(zone.characterId, zone.name)}
                          eventHandlers={{
                            click: () => {
                              setSelectedZoneId(zone.id);
                              setModalState({ zone, tab: 'log' });
                            },
                          }}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </MapContainer>
            </div>

            {/* Sidebar: desktop only */}
            <div className={cn('hidden md:flex flex-col shrink-0', zonesCollapsed ? 'w-auto' : 'w-56')}>
              {zonesCollapsed ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZonesCollapsed(false)}
                  title="Show zones"
                >
                  ‹ Zones{zones.length > 0 && ` (${zones.length})`}
                </Button>
              ) : (
                <>
                  <div className="flex items-center mb-2">
                    <div className="flex-1">{zonesHeading}</div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0 text-muted-foreground"
                      onClick={() => setZonesCollapsed(true)}
                      title="Hide zones"
                    >
                      ›
                    </Button>
                  </div>
                  {zoneListContent}
                </>
              )}
            </div>
          </div>

          {/* Zone list: mobile, shown in List view */}
          <div className={cn('md:hidden flex-col gap-2 px-4', mobileView === 'list' ? 'flex' : 'hidden')}>
            {zonesHeading}
            {zoneListContent}
          </div>
        </>
      ) : (
        // No map uploaded — list-only fallback (6.7.4).
        <div className="flex flex-col gap-2 px-4 md:px-0">
          {zonesHeading}
          {property.role === 'owner' && (
            <p className="text-xs text-muted-foreground">
              No map uploaded. Add one from the Map tab to tap zones visually.
            </p>
          )}
          {zoneListContent}
        </div>
      )}

      {disambig && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setDisambig(null)}
        >
          <div
            className="bg-background rounded-lg border border-border shadow-lg w-full max-w-xs mx-4 p-4 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold">Which zone?</h2>
            <p className="text-xs text-muted-foreground -mt-1">Overlapping zones at that point.</p>
            {disambig.map((zone) => {
              const status = zoneStatus(zone);
              return (
                <button
                  key={zone.id}
                  className="flex items-center gap-2 rounded border border-border px-3 py-2 text-left hover:bg-muted"
                  onClick={() => { setDisambig(null); setModalState({ zone, tab: 'log' }); }}
                >
                  <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground shrink-0">
                    {zone.characterId}
                  </span>
                  <span className="flex-1 truncate text-xs font-medium">{zone.name}</span>
                  <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none', status.pill)}>
                    {status.icon} {status.word}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {modalState && (
        <LogOrDeferModal
          propertyId={property.id}
          zones={sortedZones}
          selectedZone={modalState.zone}
          initialTab={modalState.tab}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attention bar — aggregates everything that needs attention: overdue zones
// (6.7.6) plus overdue equipment check-ins / due maintenance / condition-flag
// issues (6.3.2 / 6.6.4). Each segment scrolls to the relevant section/card.
// Hidden when nothing is outstanding.
// ---------------------------------------------------------------------------

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function AttentionBar({
  equipment,
  zones,
}: {
  equipment: DashboardEquipment[];
  zones: ZoneSummary[];
}) {
  // Deferred zones report `green` from the API, so red == genuinely overdue.
  // Out-of-commission equipment is excluded entirely — nothing about it is actionable.
  const overdueZones = zones.filter((z) => z.colorState === 'red');
  const inService = equipment.filter((e) => !e.outOfCommissionAt);
  const checkIns = inService.filter((e) => e.isCheckInOverdue);
  const maint = inService.filter((e) => e.nextMaintenanceDueAt && isUrgent(e.nextMaintenanceDueAt));
  const flagged = inService.filter((e) => e.notOkFlagCount > 0);
  const flagTotal = flagged.reduce((n, e) => n + e.notOkFlagCount, 0);

  const segments: { key: string; label: string; onClick: () => void }[] = [];
  if (overdueZones.length) {
    segments.push({
      key: 'zones',
      label: `${overdueZones.length} zone${overdueZones.length > 1 ? 's' : ''} overdue`,
      onClick: () => scrollToId('zones-section'),
    });
  }
  if (checkIns.length) {
    segments.push({
      key: 'checkin',
      label: `${checkIns.length} check-in${checkIns.length > 1 ? 's' : ''} overdue`,
      onClick: () => scrollToId(`equipment-${checkIns[0]!.id}`),
    });
  }
  if (maint.length) {
    segments.push({
      key: 'maint',
      label: `${maint.length} maintenance item${maint.length > 1 ? 's' : ''} due`,
      onClick: () => scrollToId(`equipment-${maint[0]!.id}`),
    });
  }
  if (flagged.length) {
    segments.push({
      key: 'flags',
      label: `${flagTotal} flag issue${flagTotal > 1 ? 's' : ''}`,
      onClick: () => scrollToId(`equipment-${flagged[0]!.id}`),
    });
  }
  if (segments.length === 0) return null;

  return (
    <div className="mx-4 md:mx-0 mb-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs shadow-sm dark:border-amber-900 dark:bg-amber-950/40">
      <span aria-hidden className="text-amber-600 dark:text-amber-400">⚠</span>
      {segments.map((s, i) => (
        <Fragment key={s.key}>
          {i > 0 && <span className="text-amber-400 dark:text-amber-700">·</span>}
          <button
            onClick={s.onClick}
            className="rounded px-1 py-1.5 font-medium text-amber-800 hover:underline dark:text-amber-200"
          >
            {s.label}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PropertyOverview() {
  const { propertyId } = Route.useParams();

  const { data: propData, isPending: propPending } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => api.get<PropertyDetailResponse>('/v1/properties/' + propertyId),
  });

  const { data: zones } = useQuery({
    queryKey: ['zones', propertyId],
    queryFn: () => api.get<ZoneSummary[]>('/v1/properties/' + propertyId + '/zones'),
  });

  const { data: equipment } = useQuery({
    queryKey: ['equipment', propertyId, false],
    queryFn: () =>
      api
        .get<{ equipment: DashboardEquipment[] }>('/v1/properties/' + propertyId + '/equipment')
        .then((r) => r.equipment),
  });

  const property = propData?.property ?? null;
  const activeZones = (zones ?? []).filter((z) => !z.archivedAt);
  const inSeasonEquipment = (equipment ?? [])
    .filter((e) => !e.archivedAt && e.isInsideSeason)
    .sort((a, b) => {
      // Urgent first, out-of-commission last, everything else in between.
      const rank = (e: DashboardEquipment) => {
        if (e.outOfCommissionAt) return 2;
        const urgent =
          e.isCheckInOverdue ||
          e.notOkFlagCount > 0 ||
          (e.nextMaintenanceDueAt !== null && isUrgent(e.nextMaintenanceDueAt));
        return urgent ? 0 : 1;
      };
      return rank(a) - rank(b);
    });

  if (propPending) {
    return <p className="text-muted-foreground p-6">Loading…</p>;
  }

  return (
    <div className="md:p-6">
      <AttentionBar equipment={inSeasonEquipment} zones={activeZones} />

      {property && (
        <div id="zones-section" className="scroll-mt-4">
          <ZonesSection property={property} zones={activeZones} />
        </div>
      )}

      {inSeasonEquipment.length > 0 && (
        <div id="equipment-section" className="mt-6 px-4 md:px-0">
          <h3 className="text-sm font-semibold mb-3">Equipment</h3>
          <div className="flex flex-wrap gap-3">
            {inSeasonEquipment.map((eq) => (
              <EquipmentQuickCard key={eq.id} eq={eq} propertyId={propertyId} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
